import { useEffect, useRef } from "react";

// Per-lead morphology: relative P / R / S / T amplitudes (approx. real 12-lead patterns)
const LEADS = [
  { label: "I",   p: 0.12, r: 0.70, s: -0.08, t: 0.20 },
  { label: "II",  p: 0.14, r: 1.00, s: -0.10, t: 0.24 },
  { label: "III", p: 0.09, r: 0.55, s: -0.15, t: 0.18 },
  { label: "aVR", p: -0.10, r: -0.62, s: -0.05, t: -0.16 },
  { label: "aVL", p: 0.06, r: 0.35, s: -0.06, t: 0.10 },
  { label: "aVF", p: 0.11, r: 0.80, s: -0.10, t: 0.20 },
  { label: "V1",  p: 0.08, r: 0.22, s: -0.85, t: 0.14 },
  { label: "V2",  p: 0.10, r: 0.38, s: -0.70, t: 0.18 },
  { label: "V3",  p: 0.12, r: 0.65, s: -0.40, t: 0.22 },
  { label: "V4",  p: 0.13, r: 0.90, s: -0.18, t: 0.24 },
  { label: "V5",  p: 0.13, r: 1.00, s: -0.08, t: 0.24 },
  { label: "V6",  p: 0.12, r: 0.92, s: -0.03, t: 0.22 },
];

const BPM = 72;
const PERIOD = 60 / BPM; // seconds per beat
const WINDOW = 2.0; // seconds visible per cell

const gauss = (x, mu, sig) => Math.exp(-((x - mu) ** 2) / (2 * sig * sig));

const beat = (t, lead) =>
  lead.p * gauss(t, 0.16, 0.022) +
  -0.06 * Math.abs(lead.r) * Math.sign(lead.r || 1) * gauss(t, 0.282, 0.006) * -1 +
  lead.r * gauss(t, 0.3, 0.007) +
  lead.s * gauss(t, 0.318, 0.008) +
  lead.t * gauss(t, 0.52, 0.04);

export const TwelveLeadEcg = ({ className = "h-[380px] lg:h-[560px]" }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let raf;
    let w = 0;
    let h = 0;
    let dpr = 1;

    const resize = () => {
      dpr = window.devicePixelRatio || 1;
      const rect = canvas.getBoundingClientRect();
      w = rect.width;
      h = rect.height;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const COLS = 3;
    const ROWS = 4;

    const draw = (now) => {
      const tt = now / 1000;
      ctx.clearRect(0, 0, w, h);

      const cellW = w / COLS;
      const cellH = h / ROWS;

      // grid
      ctx.strokeStyle = "rgba(245,241,230,0.05)";
      ctx.lineWidth = 1;
      for (let gx = 0; gx <= w; gx += 8) {
        ctx.beginPath();
        ctx.moveTo(gx + 0.5, 0);
        ctx.lineTo(gx + 0.5, h);
        ctx.stroke();
      }
      for (let gy = 0; gy <= h; gy += 8) {
        ctx.beginPath();
        ctx.moveTo(0, gy + 0.5);
        ctx.lineTo(w, gy + 0.5);
        ctx.stroke();
      }
      // cell separators
      ctx.strokeStyle = "rgba(245,241,230,0.12)";
      for (let c = 1; c < COLS; c++) {
        ctx.beginPath();
        ctx.moveTo(c * cellW + 0.5, 0);
        ctx.lineTo(c * cellW + 0.5, h);
        ctx.stroke();
      }
      for (let r = 1; r < ROWS; r++) {
        ctx.beginPath();
        ctx.moveTo(0, r * cellH + 0.5);
        ctx.lineTo(w, r * cellH + 0.5);
        ctx.stroke();
      }

      LEADS.forEach((lead, i) => {
        const col = i % COLS;
        const row = Math.floor(i / COLS);
        const x0 = col * cellW;
        const y0 = row * cellH;
        const baseline = y0 + cellH * 0.58;
        const amp = cellH * 0.34;

        // lead label
        ctx.fillStyle = "rgba(226,201,126,0.75)";
        ctx.font = "600 10px 'JetBrains Mono', monospace";
        ctx.fillText(lead.label, x0 + 8, y0 + 15);

        // trace (sweeps right-to-left, newest sample at right edge)
        ctx.beginPath();
        const steps = Math.max(2, Math.floor(cellW));
        for (let s = 0; s <= steps; s++) {
          const lt = (s / steps) * WINDOW; // seconds from left edge
          const phase = (((tt - (WINDOW - lt)) % PERIOD) + PERIOD) % PERIOD / PERIOD;
          const v = beat(phase, lead);
          const px = x0 + (s / steps) * cellW;
          const py = baseline - v * amp;
          if (s === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.strokeStyle = "rgba(212,175,55,0.95)";
        ctx.lineWidth = 1.4;
        ctx.stroke();

        // sweep head glow
        const headPhase = ((tt % PERIOD) + PERIOD) % PERIOD / PERIOD;
        const headV = beat(headPhase, lead);
        ctx.beginPath();
        ctx.arc(x0 + cellW - 2, baseline - headV * amp, 2.2, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(245,225,150,0.95)";
        ctx.fill();
      });

      // heart-rate readout
      ctx.fillStyle = "rgba(226,201,126,0.75)";
      ctx.font = "600 10px 'JetBrains Mono', monospace";
      ctx.fillText(`${BPM} bpm`, w - 52, 15);

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className={`relative w-full ${className}`}
      data-testid="ecg-12lead-canvas"
      aria-label="Live animated 12-lead ECG trace"
      role="img"
    />
  );
};
